<template>
  <div class="my-users-table">
    <div v-if="showSelectAll" class="my-users-table__select-users">
      <div>
        <template v-if="isAllSelected">All </template>
        <strong>{{ isAllSelected ? usersIds.length : selectedUsersIds.length }}</strong> users on this page are selected.
      </div>
      <div
        v-if="numberOfTotalAccounts > 50"
        class="my-users-table__select-users__all"
        @click="isAllSelected ? clearAllSelection() : selectAllUsers()"
      >
        {{ isAllSelected ? 'Clear selection' : `Select all ${numberOfTotalAccounts} users` }}
      </div>
    </div>

    <my-users-table-header
      :available-items="activeUsers"
      :selected-items="selectedUsersIds"
      @selectAll="handleSelectAll()"
      @unselectAll="handleUnselectAll()"
      @editSelectedItems="handleEditSelectedItems"
    />

    <template v-if="!loading">
      <my-users-table-row
        v-for="user in users"
        :key="user.id"
        :item="user"
        :is-selected="selectedUsersIds.includes(user.id)"
        :tooltip-props="tooltipProps"
        @select="handleSelectRow(user.id)"
        @unselect="handleUnselectRow(user.id)"
        @viewUserCart="handleViewUserCart"
        @editUserData="handleEditUserData"
      />
    </template>

    <common-loader
      v-else
      flex
      minHeightAuto
      relative
      style="padding: 100px 0"
    />

    <div v-if="!loading && showLoadMoreButton" class="my-users-table__load-more">
      <load-more
        :is-visible="showLoadMoreButton"
        :loading="loadingBtn"
        :size="loadMoreNumber"
        @more="showMoreUsers()"
      />
    </div>

    <my-users-cart-panel
      v-model="showUserCartPanel"
      :selected-user-id="editUserIdHelper"
    />

    <my-users-add-edit-user-panel
      v-model="showAddEditPanel"
      :selected-user-id="editUserIdHelper"
      @addMultipleUsers="showAddMultipleUsersPanel = true"
    />

    <my-users-manage-tags-panel
      v-model="showManageTagsPanel"
      type="users"
    />

    <my-users-add-multiple-users-panel v-model="showAddMultipleUsersPanel" />

    <my-users-edit-multiple-panel
      v-model="showEditMultiplePanel"
      :selectedUsersIds="selectedUsersIds"
    />
  </div>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import MyUsersTableHeader from './MyUsersTableHeader.vue'
import MyUsersTableRow from './MyUsersTableRow.vue'

import LoadMore from '@/components/LoadMore'
import MyUsersCartPanel from './panels/MyUsersCartPanel.vue'
import MyUsersAddEditUserPanel from './panels/MyUsersAddEditUserPanel.vue'
import MyUsersEditMultiplePanel from './panels/MyUsersEditMultiplePanel.vue'
import MyUsersAddMultipleUsersPanel from './panels/MyUsersAddMultipleUsersPanel.vue'
import MyUsersManageTagsPanel from './panels/MyUsersManageTagsPanel.vue'

const { mapState, mapGetters, mapMutations, mapActions } = createNamespacedHelpers('users')

export default {
  name: 'MyUsersTable',
  components: {
    MyUsersAddMultipleUsersPanel,
    MyUsersEditMultiplePanel,
    MyUsersAddEditUserPanel,
    MyUsersManageTagsPanel,
    MyUsersTableHeader,
    MyUsersCartPanel,
    MyUsersTableRow,
    LoadMore,
  },
  data: () => ({
    loadingBtn: false,
    editUserIdHelper: null,
    showAddEditPanel: false,
    showUserCartPanel: false,
    showEditMultiplePanel: false,
    showAddMultipleUsersPanel: false,
    showManageTagsPanel: false,
    showSelectAll: false,
  }),
  props: {
    tooltipProps: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapState([
      'users',
      'loading',
      'selectedIds',
      'usersIds',
    ]),
    ...mapGetters([
      'loadMoreNumber',
      'showLoadMoreButton',
      'numberOfTotalAccounts'
    ]),
    activeUsers () {
      return this?.users?.filter((user) => (user?.is_active ?? true)) ?? []
    },
    selectedUsersIds: {
      get () { return this.selectedIds },
      set (val) { this.setSelectedIds(val) },
    },
    isAllSelected() {
      return this.usersIds.length > 0 && this.selectedUsersIds.length === this.usersIds.length;
    },
  },
  watch: {
    showAddEditPanel: function (val) {
      if (!val) { this.editUserIdHelper = null }
    }
  },
  methods: {
    ...mapMutations([
      'addSelectedId',
      'setSelectedIds',
      'removeSelectedId',
    ]),
    ...mapActions(['loadMoreUsers', 'getUsersIds']),
    showMoreUsers () {
      this.loadingBtn = true
      this.loadMoreUsers()
        .catch((e) => (console.error(e)))
        .finally(() => (this.loadingBtn = false))
    },
    async selectAllUsers() {
      await this.getUsersIds();
      if (this.usersIds && this.usersIds.length > 0) {
        this.setSelectedIds([...this.usersIds]);
        this.showSelectAll = true;
      }
    },
    clearAllSelection() {
      this.selectedUsersIds = [];
      this.usersIds = [];
      this.showSelectAll = false;
    },
    handleSelectAll () {
      const itemsIds = this.activeUsers?.map((item) => item.id)
      this.selectedUsersIds = itemsIds
      this.showSelectAll = true;
    },
    handleUnselectAll () {
      this.selectedUsersIds = []
      this.showSelectAll = false;
    },
    handleSelectRow (userId) {
      this.addSelectedId(userId)
    },
    handleUnselectRow (userId) {
      this.removeSelectedId(userId)
    },
    handleViewUserCart (userId) {
      this.editUserIdHelper = userId
      this.showUserCartPanel = true
    },
    handleEditUserData (userId) {
      this.editUserIdHelper = userId
      this.showAddEditPanel = true
    },
    handleEditSelectedItems () {
      this.showEditMultiplePanel = true
    },
    handleManageTags () {
      this.showManageTagsPanel = true
    },
  },
}
</script>

<style lang="scss" scoped>
.my-users-table {
  display: flex;
  flex-direction: column;
  min-height: 300px;
  width: 100%;

  &__select-users {
    display: flex;
    gap: 10px;
    align-items: center;
    justify-content: center;
    background-color: #FAFAFA;
    padding: 14px 16px;
    text-align: center;
    font-weight: 400;
    font-size: 14px;
    line-height: 100%;
    letter-spacing: 0;

    > span {
      font-weight: 700;
    }

    &__all {
      color: #6E50FF;
      cursor: pointer;
    }
  }

  &__load-more {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    height: 120px;

    & > button {
      margin: 0 !important;
    }
  }
}
</style>
